import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Feather, Loader2, ShieldCheck, FileText, Landmark } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[#092B28]">
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.14]"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1491497895121-1334fc14d8c9?crop=entropy&cs=srgb&fm=jpg&q=85)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#C9A227] flex items-center justify-center">
              <Feather className="h-6 w-6 text-[#092B28]" />
            </div>
            <div>
              <div className="font-display text-2xl font-bold text-white tracking-wide">DREAMZZ TRAVELS</div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#C9A227]">Travel · Hotels · Resorts</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-bold text-white leading-tight">
            Billing, GST & accounts.<br />
            <span className="text-[#D4AF37]">One clean system.</span>
          </h1>
          <div className="space-y-3 text-sm text-[#A3B8B5]">
            <div className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[#C9A227]" /> GST-compliant invoicing with CGST / SGST / IGST engine</div>
            <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-[#C9A227]" /> Bookings, quotations, payments & expenses</div>
            <div className="flex items-center gap-3"><Landmark className="h-4 w-4 text-[#C9A227]" /> GST filing pack, ledgers & accountant-ready reports</div>
          </div>
        </div>
        <div className="relative text-xs text-[#6E8F8B]">Chikkamagaluru, Karnataka · www.dreamzztravelsindia.com</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-[#FAF8F5] lg:rounded-l-[2.5rem]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-full bg-[#C9A227] flex items-center justify-center">
              <Feather className="h-5 w-5 text-[#092B28]" />
            </div>
            <div className="font-display text-xl font-bold text-[#0D4F4A]">DREAMZZ TRAVELS</div>
          </div>
          <h2 className="font-display text-2xl font-bold text-[#1A2E2C]" data-testid="login-title">Sign in</h2>
          <p className="text-sm text-[#5C6B69] mt-1 mb-6">Access your billing & accounting workspace</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6B69]">Email</Label>
              <Input type="email" required value={form.email} autoComplete="email"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-white border-[#E5E0D8] h-11" placeholder="you@dreamzztravels.in"
                data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6B69]">Password</Label>
              <Input type="password" required value={form.password} autoComplete="current-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-white border-[#E5E0D8] h-11" placeholder="••••••••"
                data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 bg-[#0D4F4A] hover:bg-[#136A63] font-semibold"
              data-testid="login-form-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign In
            </Button>
          </form>
          <p className="text-[11px] text-[#5C6B69] mt-6 text-center">
            Role-based access · Audit logged · Secured with encrypted sessions
          </p>
        </div>
      </div>
    </div>
  );
}
